'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { HomeRes } from '@/types/api/hall/home.d';
import clsx from 'clsx';
import DotButton from '../../components/dot-button';
import { track } from '@/utils/track';
import { setWithdrawBannerSource, setDepositBannerSource } from '@/utils/banner-source';
import type { WithdrawRequestSource } from '@/utils/track';
import { CardBannerItem } from '@/features/home/services/home';
import { useThrottleByRef } from '@/features/home/hooks/use-throttle-expose';
import { BannerAutoPlayProps } from '../../const/game';
import ImageLoad from '../../components/image-load';
import './index.css';
import { AudioId, useAudioHelper } from '@/utils/audio-hepler';
import { useInViewport } from 'ahooks';
import { useModalOverlay } from '../../hooks/use-modal-overlay';
import { usePrefetchBanners } from '@/hooks/use-prefetch-banners';
import { PrefetchLink } from '@/components/prefetch-link';
import { useRouter } from '@/i18n/navigation';
import { jumpByUrl, isJumpAction, parseJumpAction } from '@/utils/jump-url';
import { fantasyGameUrlAtom } from '@/app/[locale]/(main)/(non-activity)/fantasy/store';
import { useAtom, useAtomValue } from 'jotai';
import { isLoginAtom } from '@/store';
import { JUMP_ACTION } from '@/const/jump-action';

interface BannerProps {
  bannerImages: HomeRes['top_banner'];
  from?: string;
  source?: WithdrawRequestSource;
}

export default function Banner(props: BannerProps) {
  const { bannerImages = [], from = 'home', source } = props;
  const isLogin = useAtomValue(isLoginAtom);
  const postTrackRef = useRef<boolean | undefined>(undefined);
  const bannerWrapperRef = useRef<HTMLDivElement | null>(null);
  const [isBannerInViewport] = useInViewport(bannerWrapperRef);
  const [currentSlide, setCurrentSlide] = useState(0);
  const hasModalOverlay = useModalOverlay();
  // 清除fantasy url
  const [_, setFantasyGameUrl] = useAtom(fantasyGameUrlAtom);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    loop: true,
  }, [
    Autoplay(BannerAutoPlayProps)
  ]);
  const { playAudio } = useAudioHelper({ id: AudioId.ButtonClick });
  const router = useRouter();

  // 使用 Banner 预加载 hook
  const { getBannerLink } = usePrefetchBanners(bannerImages, { enabled: true });

  const goToSlide = useCallback(
    (index: number) => {
      if (emblaApi) {
        emblaApi.scrollTo(index);
      }
    },
    [emblaApi]
  );

  const handleBannerShow = (item: CardBannerItem, index: number) => {
    if (!postTrackRef.current) return;

    track('banner_exposure', {
      current_page: from,
      current_module: item?.module_name,
      banner_number: index + 1,
      to_url: item?.link || '',
    });
  };
  // 调用 Hook，传入上报函数和等待时间
  const bannerShow = useThrottleByRef({
    wait: 20 * 1000,
  });
  const handleClick = (item: CardBannerItem, index: number) => {
    playAudio();
    const { link, has_activity_common_detail } = item || {};
    track('banner_click', {
      current_page: from,
      current_module: item?.module_name,
      banner_number: index + 1,
      to_url: link,
    });
    if (source && link && isJumpAction(link) && isLogin) {
      const { action } = parseJumpAction(link) || {};
      if (action === JUMP_ACTION.OPEN_WITHDRAW_CASH || action === JUMP_ACTION.OPEN_WITHDRAW_CRYPTO) {
        setWithdrawBannerSource(source);
      }
      if (action === JUMP_ACTION.OPEN_DEPOSIT_CASH) {
        setDepositBannerSource(source);
      }
    }

    // 处理跳转
    if (link) {
      setFantasyGameUrl('');
      jumpByUrl(link, router, item?.module_name);
    } else if (has_activity_common_detail) {
      router.push(`/banner-detail?id=${item.uuid}&type=1`);
    }
  };

  // 监听slide变化
  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      const newSlide = emblaApi.selectedScrollSnap();
      setCurrentSlide((prev) => {
        if (bannerImages?.[prev]?.uuid) {
          bannerShow(bannerImages?.[prev]?.uuid, () => {
            handleBannerShow(bannerImages?.[prev] as CardBannerItem, prev);
          });
        }
        return newSlide;
      });
    };

    emblaApi.on('select', onSelect);
    onSelect(); // 初始化时设置当前slide

    // 如果只有一张图，初始化时直接上报
    if (bannerImages?.length === 1 && bannerImages[0]?.uuid) {
      bannerShow(bannerImages[0].uuid, () => {
        handleBannerShow(bannerImages[0] as CardBannerItem, 0);
      });
    }

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, bannerImages, bannerShow]);

  useEffect(() => {
    postTrackRef.current = !!isBannerInViewport && !hasModalOverlay;
  }, [isBannerInViewport, hasModalOverlay]);

  return (
    <>
      {
        bannerImages?.length > 0 && (
          <div className="relative m-auto px-[12px] mt-[12px]" ref={bannerWrapperRef}>
            {/* 轮播图容器 */}
            <div className="embla relative overflow-hidden">
              <div className="embla__viewport h-full" ref={emblaRef}>
                <div className="embla__container flex h-full ml-[-12px]">
                  {bannerImages?.map((obj, index) => {
                    const imageElement = obj?.img ? (
                      <ImageLoad
                        skeletonClassName="absolute inset-0"
                        src={obj?.img || ''}
                        alt={`Banner ${index + 1}`}
                        className="w-full h-full object-cover"
                        priority={index < 2}
                        width={360}
                        height={170}
                      />
                    ) : (
                      <div className={clsx('w-full h-ful')} />
                    );

                    return (
                      <div key={index} className="embla__slide w-full pl-[12px] overflow-hidden">
                        <div className="aspect-[360/170] rounded-[10px] overflow-hidden relative h-full w-full flex items-center justify-center click-effect-block">
                          <PrefetchLink
                            href={getBannerLink(obj)}
                            onClick={() => handleClick(obj as CardBannerItem, index)}
                            className="w-full h-full block"
                          >
                            {imageElement}
                          </PrefetchLink>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* 指示器 */}
              {emblaApi && (
                <div className="absolute bottom-[10px] left-[15px] z-10">
                  <DotButton
                    totalSlides={bannerImages?.length || 0}
                    currentSlide={currentSlide}
                    onSlideChange={goToSlide}
                  />
                </div>
              )}
            </div>
          </div>
        )
      }
    </>
  );
}